import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import useTableSort from '@/hooks/table/use-table-sort.hook'

import { CurrencyCodeEnum, ReportsType } from '@/enums'
import { getFormatNumber } from '@/utils'
import { reportsSelector } from '@/state/reports/slices/index.slices'

import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'
import {
  GlobalReportData,
  GlobalReportLibraryColumnProps,
} from '@/state/reports/models'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

const useGlobalReports = () => {
  const { t } = useTranslation()

  const { globalReports, globalPagination, isLoading, filters } = useSelector(
    reportsSelector,
    shallowEqual,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  const getNameColumn = () => {
    switch (filters.reportType) {
      case '2':
        return t('labels.game')

      case '3':
        return t('labels.gameCategory')

      case '4':
        return t('labels.gameProvider')

      case '6':
        return t('reports.byBrand')

      default:
        return t('labels.playerId')
    }
  }

  let headCells: TableHeadCellPropsType<GlobalReportLibraryColumnProps>[] = []
  let headXlsxCells: TableHeadCellPropsType<GlobalReportLibraryColumnProps>[] =
    []

  if (
    filters.reportType !== ReportsType.BY_PLAYER_ID &&
    filters.reportType !== ReportsType.BY_ROUNDS &&
    filters.reportType !== ReportsType.BY_BRAND
  ) {
    headCells = [
      { id: 'name', label: getNameColumn() },
      { id: 'rounds', label: t('reports.rounds') },
      { id: 'bets', label: t('reports.bet') },
      { id: 'wins', label: t('reports.win') },
      { id: 'gameWin', label: t('reports.gameWin') },
      { id: 'rtp', label: t('reports.rtp') },
    ]
  } else if (filters.reportType === ReportsType.BY_PLAYER_ID) {
    headCells = [
      { id: 'playerId', label: t('labels.playerId') },
      { id: 'rounds', label: t('reports.rounds') },
      { id: 'bets', label: t('reports.bet') },
      { id: 'wins', label: t('reports.win') },
      { id: 'gameWin', label: t('reports.gameWin') },
      { id: 'rtp', label: t('reports.rtp') },
    ]
  } else if (filters.reportType === ReportsType.BY_BRAND) {
    headCells = [
      { id: 'name', label: getNameColumn() },
      { id: 'totalBets', label: t('reports.bet') },
      { id: 'totalWins', label: t('reports.win') },
      { id: 'gameWin', label: t('reports.gameWin') },
      { id: 'rtp', label: t('reports.rtp') },
    ]
  }

  headXlsxCells = [
    ...headCells,
    { id: 'currency', label: t('labels.currency') },
  ]

  const getFormattedRow = (
    report: GlobalReportData,
  ): GlobalReportLibraryColumnProps => {
    return {
      ...report,
      bets: report.bets ? Number(report.bets) : '-',
      wins: report.wins ? Number(report.wins) : '-',
      gameWin: report.gameWin ? Number(report.gameWin) : '-',
      rounds: report.rounds ? Number(report.rounds) : '-',
      totalBets: report.totalBets ? Number(report.totalBets) : '-',
      totalWins: report.totalWins ? Number(report.totalWins) : '-',
      betsText: report.bets
        ? getFormatNumber(
            Number(report.bets),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
      winsText: report.wins
        ? getFormatNumber(
            Number(report.wins),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
      totalBetsText: getFormatNumber(
        Number(report.totalBets),
        report.currency || CurrencyCodeEnum.USD,
      ),
      totalWinsText: getFormatNumber(
        Number(report.totalWins),
        report.currency || CurrencyCodeEnum.USD,
      ),
      gameWinText: report.gameWin
        ? getFormatNumber(
            Number(report.gameWin),
            report.currency || CurrencyCodeEnum.USD,
          )
        : '-',
      rtp: report.rtp ? Number(report.rtp) : '-',
      rtpText: report.rtp && `${Number(report.rtp).toFixed(2)} %`,
    }
  }

  const formattedReports =
    globalReports?.map((report) => {
      return getFormattedRow(report)
    }) || []

  const sortedInitialState = 'rounds'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    GlobalReportLibraryColumnProps,
    keyof GlobalReportLibraryColumnProps
  >(formattedReports, sortedInitialState, 'desc')

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    globalPagination,
    isLoading,
    isLoadingFull,
    headCells,
    filters,
    headXlsxCells,
    getNameColumn,
    getFormattedRow,
  }
}

export default useGlobalReports
